import React, { useMemo, useState } from 'react';
import { Card, CardBody, Col, Container, Row, Spinner, Alert, Button } from 'reactstrap';
import Breadcrumbs from 'components/Common/Breadcrumb';
import TableContainer from 'components/Common/TableContainer';
import {
  usePaginatedListQuery,
  useUpdatePatchMutation,
  useDeleteMutation,
} from 'http-client/hooks/useCrud';
import { API_ENDPOINTS } from 'http-client/httpConstants';
import { ColumnDef } from '@tanstack/react-table';
import CatalogItemCreateOrUpdate from 'components/Form/Catalog/CatalogItemCreateOrUpdate';

interface CatalogItem {
  id: string;
  name: string;
  sku: string;
  description: string;
  imageUrl: string;
  categoryId: string;
  categoryName: string;
  isActive: boolean;
}

const CatalogItemsList: React.FC = () => {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<string | undefined>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<string | undefined>(undefined);

  const { data, isLoading, isError, error, refetch } = usePaginatedListQuery<CatalogItem>(
    API_ENDPOINTS.SAAS.CATALOG_ITEMS.BASE,
    {
      pageNumber,
      pageSize,
      search: search || undefined,
      sortBy,
      sortDirection,
    },
  );

  const catalogItems: CatalogItem[] = useMemo(() => {
    return data?.items ?? [];
  }, [data]);

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
    if (isModalOpen) {
      setSelectedItemId(undefined);
    }
  };

  const handleAddNew = () => {
    setSelectedItemId(undefined);
    setIsModalOpen(true);
  };

  const handleEdit = (id: string) => {
    setSelectedItemId(id);
    setIsModalOpen(true);
  };

  const handleModalSuccess = () => {
    refetch();
  };

  const columns: ColumnDef<CatalogItem>[] = useMemo(
    () => [
      {
        header: 'Name',
        accessorKey: 'name',
      },
      {
        header: 'SKU',
        accessorKey: 'sku',
      },
      {
        header: 'Category',
        accessorKey: 'categoryName',
      },
      {
        header: 'Description',
        accessorKey: 'description',
      },
      {
        header: 'Status',
        accessorKey: 'isActive',
        cell: ({ row }) => <span>{row.original.isActive ? 'Active' : 'Inactive'}</span>,
      },
      {
        header: 'Actions',
        id: 'actions',
        cell: ({ row }) => {
          const { id, isActive } = row.original;
          const { mutate: toggleStatus } = useUpdatePatchMutation(
            `${API_ENDPOINTS.SAAS.CATALOG_ITEMS.BASE}/${id}`,
            API_ENDPOINTS.SAAS.CATALOG_ITEMS.BASE,
          );

          const { mutate: deleteCatalogItem } = useDeleteMutation(
            `${API_ENDPOINTS.SAAS.CATALOG_ITEMS.BASE}/${id}`,
            API_ENDPOINTS.SAAS.CATALOG_ITEMS.BASE,
          );

          const handleToggleStatus = () => {
            if (
              window.confirm(
                `Are you sure you want to ${isActive ? 'deactivate' : 'activate'} this item?`,
              )
            ) {
              toggleStatus({ data: { isActive: !isActive }, params: { id } });
            }
          };

          const handleDelete = () => {
            if (window.confirm('Are you sure you want to delete this catalog item?')) {
              deleteCatalogItem(id);
            }
          };

          return (
            <div className="d-flex gap-2 align-items-center">
              <Button size="sm" color="primary" outline onClick={() => handleEdit(id)}>
                Edit
              </Button>
              <i
                className={`bx ${isActive ? 'bx-check-circle' : 'bx-x-circle'} ${
                  isActive ? 'text-success' : 'text-warning'
                } cursor-pointer`}
                title={isActive ? 'Deactivate' : 'Activate'}
                style={{ fontSize: '1.2rem' }}
                onClick={handleToggleStatus}
              ></i>
              <i
                className="bx bx-trash text-muted cursor-pointer"
                title="Delete"
                style={{ fontSize: '1.2rem' }}
                onClick={handleDelete}
              ></i>
            </div>
          );
        },
      },
    ],
    [],
  );

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Catalog Items" breadcrumbItem="List" />

        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                {isLoading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                  </div>
                ) : isError ? (
                  <Alert color="danger">
                    {error?.message || 'Failed to load catalog items. Please try again later.'}
                  </Alert>
                ) : (
                  <>
                    <div className="mb-3">
                      <Button color="success" onClick={handleAddNew}>
                        Add Catalog Item
                      </Button>
                    </div>
                    <TableContainer
                      columns={columns}
                      data={catalogItems}
                      isGlobalFilter={true}
                      isAddButton={false}
                      isPagination={true}
                      isCustomPageSize={true}
                      buttonClass="btn btn-success btn-rounded"
                      buttonName="Add Catalog Item"
                      tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-borderless"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                      paginationClass="pagination"
                      SearchPlaceholder="Search catalog items..."
                    />

                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <small className="text-muted">
                        Showing {catalogItems.length > 0 ? (pageNumber - 1) * pageSize + 1 : 0} to{' '}
                        {Math.min(pageNumber * pageSize, data?.totalCount || 0)} of{' '}
                        {data?.totalCount || 0} items
                      </small>

                      <div className="d-flex gap-2">
                        <Button
                          size="sm"
                          outline
                          color="primary"
                          onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                          disabled={!data?.hasPreviousPage || isLoading}
                        >
                          Previous
                        </Button>
                        <span className="align-self-center small">
                          Page {data?.pageNumber || 1} of {data?.totalPages || 1}
                        </span>
                        <Button
                          size="sm"
                          outline
                          color="primary"
                          onClick={() => setPageNumber((p) => p + 1)}
                          disabled={!data?.hasNextPage || isLoading}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <CatalogItemCreateOrUpdate
        isOpen={isModalOpen}
        toggle={toggleModal}
        itemId={selectedItemId}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};

export default CatalogItemsList;
